import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import {
	MessageSquare,
	Send,
	Trash2,
	Edit2,
	X,
	Check,
	RefreshCw,
	CheckCircle,
	XCircle,
	Info,
	Clock,
	User,
} from 'lucide-react'
import { Button, ConfirmDialog } from '@components/common'
import authService from '@services/authService'
import taskService from '@services/taskService'
import './TaskNotesSection.scss'

/**
 * TaskNotesSection Component
 *
 * Displays conversation-style notes thread for a task
 * Features:
 * - Add new comments
 * - Edit own comments
 * - View history of notes (comments, status changes, approvals, rejections)
 * - Delete own notes
 * - Auto-refresh capability
 */
const TaskNotesSection = ({ taskId, isLocked = false }) => {
	// Get user directly from authService (more reliable than context)
	const user = authService.getCurrentUser()
	const [notes, setNotes] = useState([])
	const [newNote, setNewNote] = useState('')
	const [loading, setLoading] = useState(true)
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)
	const [editingNoteId, setEditingNoteId] = useState(null)
	const [editNoteText, setEditNoteText] = useState('')
	const [savingEdit, setSavingEdit] = useState(false)
	const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, note: null })
	const [deleting, setDeleting] = useState(false)
	const notesEndRef = useRef(null)
	const editTextareaRef = useRef(null)

	useEffect(() => {
		if (taskId) {
			fetchNotes()
		}
	}, [taskId])

	// Focus textarea when entering edit mode
	useEffect(() => {
		if (editingNoteId && editTextareaRef.current) {
			editTextareaRef.current.focus()
			// Move cursor to end
			const len = editTextareaRef.current.value.length
			editTextareaRef.current.setSelectionRange(len, len)
		}
	}, [editingNoteId])

	const fetchNotes = async () => {
		try {
			setLoading(true)
			setError(null)
			const response = await taskService.getNotes(taskId)
			if (response.status === 'success' || response.success) {
				setNotes(response.data || [])
			} else {
				setError('Failed to load notes')
			}
		} catch (err) {
			console.error('Error fetching notes:', err)
			setError('Failed to load notes')
		} finally {
			setLoading(false)
		}
	}

	const handleAddNote = async (e) => {
		e.preventDefault()

		if (!newNote.trim()) return

		setSubmitting(true)
		try {
			const response = await taskService.addNote(taskId, {
				note: newNote.trim(),
				note_type: 'comment',
			})

			if (response.status === 'success' || response.success) {
				setNewNote('')
				await fetchNotes()
				// Scroll to bottom after adding note
				setTimeout(() => {
					notesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
				}, 100)
			}
		} catch (err) {
			console.error('Error adding note:', err)
			setError('Failed to add note')
		} finally {
			setSubmitting(false)
		}
	}

	const handleStartEdit = (note) => {
		setEditingNoteId(note.id)
		setEditNoteText(note.note)
	}

	const handleCancelEdit = () => {
		setEditingNoteId(null)
		setEditNoteText('')
	}

	const handleSaveEdit = async (noteId) => {
		if (!editNoteText.trim()) {
			setError('Note content cannot be empty')
			return
		}

		setSavingEdit(true)
		try {
			const response = await taskService.updateNote(taskId, noteId, {
				note: editNoteText.trim(),
			})

			if (response.status === 'success' || response.success) {
				setEditingNoteId(null)
				setEditNoteText('')
				await fetchNotes()
			} else {
				setError(response.message || 'Failed to update note')
			}
		} catch (err) {
			console.error('Error updating note:', err)
			setError(err.message || 'Failed to update note')
		} finally {
			setSavingEdit(false)
		}
	}

	const handleDeleteClick = (note) => {
		setDeleteConfirm({ isOpen: true, note })
	}

	const handleDeleteConfirm = async () => {
		if (!deleteConfirm.note) return

		setDeleting(true)
		try {
			const response = await taskService.deleteNote(taskId, deleteConfirm.note.id)
			if (response.status === 'success' || response.success) {
				await fetchNotes()
				setDeleteConfirm({ isOpen: false, note: null })
			} else {
				setError('Failed to delete note')
			}
		} catch (err) {
			console.error('Error deleting note:', err)
			setError('Failed to delete note')
		} finally {
			setDeleting(false)
		}
	}

	const handleDeleteCancel = () => {
		if (!deleting) {
			setDeleteConfirm({ isOpen: false, note: null })
		}
	}

	const getNoteIcon = (noteType) => {
		switch (noteType) {
			case 'approval':
				return <CheckCircle size={16} className="note-icon note-icon--approval" />
			case 'rejection':
				return <XCircle size={16} className="note-icon note-icon--rejection" />
			case 'status_change':
				return <RefreshCw size={16} className="note-icon note-icon--status" />
			case 'system':
				return <Info size={16} className="note-icon note-icon--system" />
			default:
				return <MessageSquare size={16} className="note-icon note-icon--comment" />
		}
	}

	const formatTimestamp = (dateString) => {
		if (!dateString) return ''
		// Backend returns UTC timestamps without timezone indicator (e.g., "2025-12-17 07:40:11")
		// Append 'Z' to indicate UTC if not already present, and replace space with 'T' for ISO format
		const utcDateString = dateString.includes('Z') || dateString.includes('+') || dateString.includes('-', 10)
			? dateString
			: dateString.replace(' ', 'T') + 'Z'
		const date = new Date(utcDateString)
		const now = new Date()
		const diffMs = now - date
		const diffMins = Math.floor(diffMs / 60000)
		const diffHours = Math.floor(diffMs / 3600000)
		const diffDays = Math.floor(diffMs / 86400000)

		if (diffMins < 1) return 'Just now'
		if (diffMins < 60) return `${diffMins}m ago`
		if (diffHours < 24) return `${diffHours}h ago`
		if (diffDays < 7) return `${diffDays}d ago`

		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
		})
	}

	// Check if user can edit a note (only own comment notes, not locked)
	const canEditNote = (note) => {
		if (isLocked) return false
		if (note.note_type !== 'comment') return false
		if (!user) return false
		// Compare as numbers to handle string vs int mismatch
		return Number(note.user_id) === Number(user.id)
	}

	const canDeleteNote = (note) => {
		if (note.note_type === 'system') return false
		if (!user) return false
		// Compare as numbers to handle string vs int mismatch
		const roleSlug = user.role_slug || user.role?.slug
		return Number(note.user_id) === Number(user.id) || roleSlug === 'super-admin' || roleSlug === 'company-admin'
	}

	if (loading) {
		return (
			<div className="task-notes-section">
				<div className="task-notes-section__header">
					<h3>
						<MessageSquare size={18} />
						Notes & Conversation
					</h3>
				</div>
				<div className="task-notes-section__loading">
					<div className="spinner"></div>
					<span>Loading notes...</span>
				</div>
			</div>
		)
	}

	return (
		<div className="task-notes-section">
			<div className="task-notes-section__header">
				<h3>
					<MessageSquare size={18} />
					Notes & Conversation
					{notes.length > 0 && <span className="count-badge">{notes.length}</span>}
				</h3>
				<Button variant="ghost" size="small" onClick={fetchNotes} disabled={loading}>
					<RefreshCw size={14} />
				</Button>
			</div>

			{error && (
				<div className="task-notes-section__error">
					{error}
					<button onClick={() => setError(null)}>&times;</button>
				</div>
			)}

			<div className="task-notes-section__list">
				{notes.length === 0 ? (
					<div className="task-notes-section__empty">
						<MessageSquare size={32} />
						<p>No notes yet</p>
						<span>Add a note to start the conversation</span>
					</div>
				) : (
					notes.map((note) => (
						<div
							key={note.id}
							className={`note-item note-item--${note.note_type} ${
								user && Number(note.user_id) === Number(user.id) ? 'note-item--own' : ''
							} ${editingNoteId === note.id ? 'note-item--editing' : ''}`}
						>
							<div className="note-item__avatar">
								{note.user_profile_image ? (
									<img src={note.user_profile_image} alt={note.user_name} />
								) : (
									<div className="note-item__avatar-placeholder">
										{note.note_type === 'system' ? (
											<Info size={16} />
										) : (
											<User size={16} />
										)}
									</div>
								)}
							</div>

							<div className="note-item__content">
								<div className="note-item__bubble">
									<div className="note-item__header">
										<span className="note-item__author">
											{note.note_type === 'system' ? 'System' : note.user_name || 'Unknown'}
										</span>
										{note.user_role && note.note_type !== 'system' && (
											<span className="note-item__role">{note.user_role}</span>
										)}
										<span className="note-item__time">
											<Clock size={12} />
											{formatTimestamp(note.created_at)}
											{note.updated_at && note.updated_at !== note.created_at && (
												<span className="edited-label">(edited)</span>
											)}
										</span>
									</div>

									{editingNoteId === note.id ? (
										<div className="note-item__edit-form">
											<textarea
												ref={editTextareaRef}
												value={editNoteText}
												onChange={(e) => setEditNoteText(e.target.value)}
												rows={3}
												disabled={savingEdit}
												placeholder="Enter your note..."
											/>
											<div className="note-item__edit-actions">
												<button
													type="button"
													className="edit-action-btn edit-action-btn--save"
													onClick={() => handleSaveEdit(note.id)}
													disabled={savingEdit || !editNoteText.trim()}
													title="Save"
												>
													{savingEdit ? (
														<RefreshCw size={14} className="spinning" />
													) : (
														<Check size={14} />
													)}
												</button>
												<button
													type="button"
													className="edit-action-btn edit-action-btn--cancel"
													onClick={handleCancelEdit}
													disabled={savingEdit}
													title="Cancel"
												>
													<X size={14} />
												</button>
											</div>
										</div>
									) : (
										<div className="note-item__body">
											{getNoteIcon(note.note_type)}
											<p>{note.note}</p>
										</div>
									)}

									{note.step_name && (
										<div className="note-item__step">
											Step: {note.step_name}
										</div>
									)}
								</div>
							</div>

							{!editingNoteId && (canEditNote(note) || canDeleteNote(note)) && !isLocked && (
								<div className="note-item__actions">
									{canEditNote(note) && (
										<button
											className="note-item__action-btn"
											onClick={() => handleStartEdit(note)}
											title="Edit note"
										>
											<Edit2 size={14} />
										</button>
									)}
									{canDeleteNote(note) && (
										<button
											className="note-item__action-btn note-item__action-btn--delete"
											onClick={() => handleDeleteClick(note)}
											title="Delete note"
										>
											<Trash2 size={14} />
										</button>
									)}
								</div>
							)}
						</div>
					))
				)}
				<div ref={notesEndRef} />
			</div>

			{!isLocked && (
				<form className="task-notes-section__form" onSubmit={handleAddNote}>
					<div className="note-input-wrapper">
						<textarea
							value={newNote}
							onChange={(e) => setNewNote(e.target.value)}
							placeholder="Add a note or comment..."
							rows={2}
							disabled={submitting}
						/>
						<Button
							type="submit"
							variant="primary"
							size="small"
							disabled={submitting || !newNote.trim()}
						>
							{submitting ? (
								<RefreshCw size={16} className="spinning" />
							) : (
								<Send size={16} />
							)}
						</Button>
					</div>
				</form>
			)}

			{isLocked && (
				<div className="task-notes-section__locked">
					<Info size={16} />
					<span>This task is locked. Notes cannot be added.</span>
				</div>
			)}

			{/* Delete Confirmation Dialog */}
			<ConfirmDialog
				isOpen={deleteConfirm.isOpen}
				onConfirm={handleDeleteConfirm}
				onCancel={handleDeleteCancel}
				title="Delete Note"
				message={
					deleteConfirm.note
						? `Are you sure you want to delete this note? This action cannot be undone.`
						: 'Are you sure you want to delete this note?'
				}
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
				loading={deleting}
			/>
		</div>
	)
}

TaskNotesSection.propTypes = {
	taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	isLocked: PropTypes.bool,
}

export default TaskNotesSection
